import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Lesson, LessonSlots } from './interfaces'

import { Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { LessonEntryAction, LessonsActionModal } from './enums'
import { useLessonEditStore } from './edit/store'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { useDisciplineRefresh } from '../discipline-refresh.hook'
import { LESSON_ACTION_QUERY, getLessonEntryAction, isReservedLesson } from './lessons'

interface LessonEditActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  refLesson: ComputedRef<Lesson | null>
  refSlots: ComputedRef<LessonSlots | null>
  refActionModal: ComputedRef<LessonsActionModal | null>
  refCanManage: ComputedRef<boolean>
  refCanViewChangelog: ComputedRef<boolean>
  refCanDelete: ComputedRef<boolean>
  refIsChangeDisabled: ComputedRef<boolean>
  refIsDeleteDisabled: ComputedRef<boolean>
  refIsShown: ComputedRef<boolean>
  toggle: () => void
  closeMenu: () => void
  openReschedule: () => void
  openCancel: () => void
  openChangelog: () => Promise<void>
  closeActionModal: () => void
  afterActionSave: () => Promise<void>
}

/** The actions menu of the lesson page: what the row menu of the Lessons tab offers besides editing, with the same
 *  rights and the same locks. A cancel or a reschedule done here leads back to the tab, where the lesson now reads
 *  differently. */
export const useLessonEditActions = (): LessonEditActionsReturn => {
  const route = useRoute()
  const store = useLessonEditStore()
  const { canDeleteBookings, canViewPaidBookings, canManageBookings } = useLessonPrivileges()
  const { refreshDiscipline } = useDisciplineRefresh()

  const disciplineId = route.params.id as string

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const refLesson = computed<Lesson | null>(() => store.getLesson)

  const refSlots = computed<LessonSlots | null>(() => store.getSlots)

  const refActionModal = computed<LessonsActionModal | null>(() => store.getActionModal)

  const refCanManage = computed<boolean>(() => canManageBookings.value)

  /** The changelog needs view-paid-bookings even for demo lessons; sales agents do not hold it. */
  const refCanViewChangelog = computed<boolean>(() => canViewPaidBookings.value)

  const refCanDelete = computed<boolean>(() => canDeleteBookings.value)

  /** A completed or reserved lesson can be neither canceled nor rescheduled, as in the row menu. */
  const refIsChangeDisabled = computed<boolean>(() => {
    const status = refLesson.value?.status
    return !status || status === BookingStatus.Completed || isReservedLesson(status)
  })

  const refIsDeleteDisabled = computed<boolean>(() => !refLesson.value || isReservedLesson(refLesson.value.status))

  const refIsShown = computed<boolean>(() =>
    !!refLesson.value && (refCanManage.value || refCanViewChangelog.value || refCanDelete.value))

  const toggle = (): void => {
    if (store.isSaving) return
    isOpen.value = !isOpen.value
  }

  const closeMenu = (): void => {
    isOpen.value = false
  }

  const openReschedule = (): void => {
    closeMenu()
    store.setActionModal(LessonsActionModal.Reschedule)
  }

  const openCancel = (): void => {
    closeMenu()
    store.setActionModal(LessonsActionModal.Cancel)
  }

  const openChangelog = async (): Promise<void> => {
    closeMenu()

    const url = refLesson.value ? resolveUrl(AppRoute.BookingsChangelog, { id: refLesson.value.id }) : null
    if (url) await navigateTo(url)
  }

  const closeActionModal = (): void => {
    store.setActionModal(null)
  }

  const afterActionSave = async (): Promise<void> => {
    // The discipline is reread once the Lessons tab is up: its page loader covers that read, whereas here it would
    // rise over the open modal.
    await navigateTo(resolveUrl(AppRoute.DisciplinesLessons, { id: disciplineId }) ?? '/')
    void refreshDiscipline()
  }

  /** A row of the Lessons tab leads here with the window to open, with the same rights and locks as this menu; the URL
   *  is then cleaned so a reload opens nothing. */
  const openEntryAction = (): void => {
    const action = getLessonEntryAction(route.query[LESSON_ACTION_QUERY])
    if (!action) return

    if (action === LessonEntryAction.Delete) {
      if (refCanDelete.value && !refIsDeleteDisabled.value) store.setDeleteConfirmOpen(true)
    } else if (refCanManage.value && !refIsChangeDisabled.value) {
      store.setActionModal(action === LessonEntryAction.Cancel ? LessonsActionModal.Cancel : LessonsActionModal.Reschedule)
    }

    const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => key !== LESSON_ACTION_QUERY))
    void navigateTo({ path: route.path, query }, { replace: true })
  }

  openEntryAction()

  onClickOutside(containerRef, closeMenu)

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    refLesson,
    refSlots,
    refActionModal,
    refCanManage,
    refCanViewChangelog,
    refCanDelete,
    refIsChangeDisabled,
    refIsDeleteDisabled,
    refIsShown,
    toggle,
    closeMenu,
    openReschedule,
    openCancel,
    openChangelog,
    closeActionModal,
    afterActionSave,
  }
}
